import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  typeguards as commonTypeguards,
  date as commonDate,
  finleyAssertUnreachable,
  debugLog,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';
import {
  ValleyBankPipelineStatusMetadata,
  ValleyBankPortfolioReceivableResponseTapeStats,
  typeguards,
  parse,
} from '@finley-technologies/valley-bank-utils';

import { useGetValleyBankPipelineStatusMetadataQuery } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  DahsboardModeWithDate,
  DashboardMode,
  DataMetadataContextType,
} from './DataMetadataContextType';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { useQueryParams } from '~/packages/common-frontend/hooks/useQueryParams';

// reporting date for "current"
const currentReportingDate: Util.FullDateStr = commonDate.addDays(
  commonDate.currentDateStr(),
  -1,
);

// default date to use when setting historical mode initially
const previousReportingDate: Util.FullDateStr = commonDate.addDays(
  currentReportingDate,
  -1,
);

export const DataMetadataContext = createContext<DataMetadataContextType>({
  status: 'in-progress',
  currentReportingDate,
  reportingDate: currentReportingDate,
  asOfDate: null,
  dashboardMode: 'current',
  setAsOfDate: () => {},
  setDashboardMode: () => {},
  setIsAssignmentSubmitted: () => {},
  setReportingDate: () => {},
  portfolioStats: null,
  metadata: null,
  isPortfolioStatsLoading: false,
  isPreview: false,
});

export const useDataMetadataContext = () => {
  const context = useContext(DataMetadataContext);
  if (context === null) {
    throw new Error('DataMetadataContext must be present');
  }

  return context;
};

export const MODE_URL_PARAM = 'mode';

type DataMetadataProviderProps = { children: React.ReactNode };
export const DataMetadataProvider = ({
  children,
}: DataMetadataProviderProps) => {
  const toast = useToast();
  const [asOfDate, setAsOfDate] = useState<Util.FullDateStr | null>(null);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [reportingDate, setReportingDate] = useState<Util.FullDateStr>(
    currentReportingDate,
  );

  const query = useQueryParams();

  const navigate = useNavigate();
  const location = useLocation();

  const [metadata, setMetadata] = useState<
    ValleyBankPipelineStatusMetadata['metadata'] | null
  >(null);
  const [
    portfolioStats,
    setPortfolioStats,
  ] = useState<ValleyBankPortfolioReceivableResponseTapeStats | null>(null);

  /**
   * This property will be used by the assignment page to bridge the gap between when a user submits a new assignment and when the pipeline portfolio stats record is inserted into the db when pipeline starts
   */
  const [isAssignmentSubmitted, setIsAssignmentSubmitted] = useState<boolean>(
    false,
  );

  const {
    data,
    loading: isPortfolioStatsLoading,
    startPolling,
    stopPolling,
  } = useGetValleyBankPipelineStatusMetadataQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    variables: {
      reportingDate,
      isPreview,
    },
  });

  useEffect(() => {
    startPolling(5000);
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  useEffect(() => {
    if (data?.valleyBankPipelineStatusMetadata === 'null') {
      // temp log to confirm the fix.
      debugLog(
        'valleyBankPipelineStatusMetadata received null string, skipping parse',
      );
    }
    if (
      data?.valleyBankPipelineStatusMetadata &&
      data.valleyBankPipelineStatusMetadata !== 'null'
    ) {
      try {
        const pipelineStatusMetadata = parse.parseValleyBankPipelineStatusMetadata(
          data.valleyBankPipelineStatusMetadata,
        );

        const {
          metadata: newMetadata,
          portfolioStats: newPortfolioStats,
        } = pipelineStatusMetadata;

        setMetadata(newMetadata);

        if (
          typeguards.isValleyBankPortfolioReceivableResponseTapeMetadataStatusInProgress(
            newMetadata.status,
          )
        ) {
          // once we see the pipeline kickoff after an assignment is submitted, we want to turn this flag off
          setIsAssignmentSubmitted(false);
        }

        if (
          typeguards.isValleyBankPortfolioReceivableResponseTapeStats(
            newPortfolioStats,
          )
        ) {
          setPortfolioStats(newPortfolioStats);
        }
      } catch (err) {
        Sentry.captureException(err);
      }
    } else {
      setMetadata(null);
      setPortfolioStats(null);
      setAsOfDate(null);
    }
  }, [data]);

  // read mode/date from the url query params if provided
  useEffect(() => {
    // Extract and set the initial mode or date based on the URL when the component mounts
    const mode = query.get(MODE_URL_PARAM);
    if (mode) {
      switch (mode) {
        case 'current':
        case 'preview':
          setIsPreview(mode === 'preview');
          setReportingDate(currentReportingDate);
          break;
        default:
          if (commonTypeguards.isFullDateStr(mode)) {
            setIsPreview(false);
            setReportingDate(mode);
          }
          break;
      }
    }
  }, [query]);

  const context: DataMetadataContextType = useMemo(() => {
    const setDashboardModeExternal = (
      newDashboardMode: DashboardMode,
    ): void => {
      // generate query parameters from newDashboardMode
      let modeWithDate: DahsboardModeWithDate = 'current';
      switch (newDashboardMode) {
        case 'current':
          modeWithDate = 'current';
          break;
        case 'preview':
          modeWithDate = 'preview';
          break;
        case 'historical':
          modeWithDate = previousReportingDate;
          break;
        default:
          finleyAssertUnreachable(newDashboardMode);
      }
      const queryParameters = new URLSearchParams(location.search);
      queryParameters.set(MODE_URL_PARAM, modeWithDate);
      navigate({
        pathname: location.pathname,
        search: queryParameters.toString(),
      });
    };

    const setReportingDateExternal = (
      newReportingDate: Util.FullDateStr,
    ): void => {
      // generate query parameters from newReportingDate
      const queryParameters = new URLSearchParams(location.search);
      queryParameters.set(MODE_URL_PARAM, newReportingDate);
      navigate({
        pathname: location.pathname,
        search: queryParameters.toString(),
      });
    };

    const getDashboardMode = (): DashboardMode => {
      if (isPreview) {
        return 'preview';
      }
      if (reportingDate === currentReportingDate) {
        return 'current';
      }
      return 'historical';
    };

    let status: DataMetadataContextType['status'] = 'ready';
    if (
      typeguards.isValleyBankPortfolioReceivableResponseTapeMetadataStatusFailure(
        metadata?.status,
      )
    ) {
      status = 'error';
    } else if (
      !metadata ||
      isAssignmentSubmitted ||
      (!isPortfolioStatsLoading &&
        !typeguards.isValleyBankPortfolioReceivableResponseTapeMetadataStatusSuccess(
          metadata?.status,
        ))
    ) {
      status = 'in-progress';
    }

    return {
      status,
      currentReportingDate,
      reportingDate,
      setReportingDate: setReportingDateExternal,
      asOfDate,
      dashboardMode: getDashboardMode(),
      isPreview,
      metadata,
      portfolioStats,
      isPortfolioStatsLoading,
      setAsOfDate: (newAsOfDate: Util.Maybe<Util.FullDateStr>) => {
        if (newAsOfDate !== undefined) {
          setAsOfDate(newAsOfDate);
        }
      },
      setDashboardMode: setDashboardModeExternal,
      setIsAssignmentSubmitted,
    };
  }, [
    isAssignmentSubmitted,
    reportingDate,
    asOfDate,
    isPreview,
    metadata,
    portfolioStats,
    isPortfolioStatsLoading,
    location.search,
    location.pathname,
    navigate,
  ]);

  return (
    <DataMetadataContext.Provider value={context}>
      {children}
    </DataMetadataContext.Provider>
  );
};
